import moment, { DurationInputArg1, DurationInputArg2 } from 'moment-timezone';
import { FINTECH_MONTH_COUNT, USER_METRICS_MONTH_COUNT, FilterDateFormat_MMM_YYYY } from 'src/components/fintech-ops/FinTechOpsConstants';
import { logger } from 'src/logger';
// Returns current UTC time in below format
// example: Jan 22nd 2023, 02:15:35 PM UTC
export const getCurrentUTCTimeZone = () => moment.utc().format('MMM Do YYYY, hh:mm:ss A z');

export const getCurrentUserTimeZone = () => moment().tz(moment.tz.guess()).format('YYYY-MM-DD HH:mm:ss');

// Returns current UTC time in ISO format
export const getCurrentUTCTimeZoneInISO = () => moment.utc().toISOString();

export const convertUTCtoUserTimeZone = (utcTimeStamp: string) => {
  // Detect user's local timezone
  const userTimezone = moment.tz.guess();

  // Convert UTC to user's local timestamp
  const localTimestamp = moment.utc(utcTimeStamp).tz(userTimezone).format();

  return localTimestamp;
};

export const convertUTCtoUserTimeZoneMoment = (utcTimeStamp: string) => {
  // Detect user's local timezone
  const userTimezone = moment.tz.guess();

  // Convert UTC to user's local timestamp
  return moment.utc(utcTimeStamp).tz(userTimezone);
};

export const getDateFromDateTime = (value: string) => moment(value).format('YYYY-MM-DD');

// Returns like July 16, 2023
export const getReadableDateFromDateTime = (value: string) => moment(value).format('ll');

// Returns like "May 21, 2026, 6:27 PM PDT" in the user's local timezone.
export const getReadableDateTimeShortFormat = (inputValue?: string): string => {
  if (!inputValue) return '';
  const localTime = moment(inputValue.toString()).tz(moment.tz.guess());
  return localTime.format('MMM D, YYYY, h:mm A z');
};

export const getReadableDateTimeFormat = (inputValue?: string) => {
  if (inputValue) {
    const inputValueInMoment = moment(inputValue?.toString());
    const localTime = inputValue ? inputValueInMoment.tz(moment.tz.guess()) : moment.tz(moment.tz.guess());
    return localTime.format('MMM Do YYYY, hh:mm:ss A z');
  } else {
    return '';
  }
};

export const dateTimeComparatorForTable = (dateTime1: string, dateTime2: string) => {
  const date1 = moment(dateTime1);
  const date2 = moment(dateTime2);
  return date1.isBefore(date2) ? -1 : 1;
};

export const dateTimeComparator = (dateTime1: string, dateTime2: string) => {
  const date1 = moment(dateTime1);
  const date2 = moment(dateTime2);
  return date1.isBefore(date2) ? 1 : -1;
};

export const dateTimeComparatorMMMYYYYFormat = (dateTime1: string, dateTime2: string) => {
  const date1 = moment(dateTime1, FilterDateFormat_MMM_YYYY);
  const date2 = moment(dateTime2, FilterDateFormat_MMM_YYYY);
  return date1.isBefore(date2) ? 1 : -1;
};

export const sortedDateArrayMMMYYYYFormat = (array: any[]) => {
  return array?.sort((a, b) => dateTimeComparatorMMMYYYYFormat(a, b));
};

export const getUserTimeZoneInMomentObject = (inputValue?: string) => {
  const inputValueInMoment = moment(inputValue?.toString());
  const localTime = inputValue ? inputValueInMoment.tz(moment.tz.guess()) : moment.tz(moment.tz.guess());
  return localTime;
};

export const getCurrentUserLocalTimeReadableFormat = (inputValue?: string) => {
  if (!inputValue) {
    return '';
  }

  const inputValueInMoment = moment(inputValue?.toString());
  const localTime = inputValue ? inputValueInMoment.tz(moment.tz.guess()) : moment.tz(moment.tz.guess());
  return localTime.format('MMM Do YYYY, hh:mm:ss A z');
};

export const getMonth = (value: string) => moment(value).format('MMM');

export const getYear = (value: string) => moment(value).format('YYYY');

export const getDay = (value: string) => moment(value).format('DD');

export const epochToUserTimeZone = (input: string): string => {
  const formattedTime = moment(parseInt(input).valueOf() * 1000).local();
  return getCurrentUserLocalTimeReadableFormat(formattedTime.toISOString());
};

export const getCurrentTime = () => moment();

export const getTimeDifference = (input: moment.Moment) => {
  return moment().diff(input, 'millisecond');
};

export const getPreviousPeriod = (subtraction: DurationInputArg1, unit: DurationInputArg2) => {
  return moment().subtract(subtraction, unit);
};

export const getMomentTime = (input: string) => {
  return moment(input);
};

// export const getFormattedTime = (input: string, format: string) => {
//   return moment(input).format(format);
// };

export const getBackAgeInPeriod = (startTime: moment.MomentInput, unit: DurationInputArg2, endTime?: moment.MomentInput) => {
  if (endTime) {
    return moment(endTime).diff(moment(startTime), unit);
  } else {
    return moment().diff(moment(startTime), unit);
  }
};

/** A working day is 8 SLA-hours; weekends (Sat/Sun) accrue zero. */
export const BUSINESS_HOURS_PER_DAY = 8;

/**
 * Elapsed **business** time in seconds between two ISO timestamps, for SLA calculations.
 *
 * Model (per product decision): each weekday counts as 8 SLA-hours, Sat/Sun accrue zero.
 * Business time therefore accrues continuously on weekdays at 8/24 of wall-clock (so a full
 * weekday = 8 SLA-hours), and pauses entirely across weekends. Example: a ticket opened
 * Mon 8am has burned exactly 24 SLA-hours by Thu 8am (Mon→Tue, Tue→Wed, Wed→Thu = 3 × 8h),
 * so the Sev 3 (24 working-hour) SLA is met iff it resolves before then.
 *
 * Weekend/day boundaries are evaluated in the START timestamp's own UTC offset
 * (`moment.parseZone`), so "same timezone as the created date" holds regardless of whether
 * the pipeline emitted a `Z` (UTC) or offset (e.g. `-07:00`) timestamp. Returns 0 for a
 * missing/invalid input or a non-positive span.
 *
 * Note: `moment.parseZone` preserves the input's fixed offset and does NOT apply DST — a
 * `Z` (UTC) timestamp therefore has its weekday boundaries judged in UTC, not the caller's
 * local time. This is the intended behaviour for pipeline-emitted timestamps (weekend
 * exclusion is anchored to the offset the data carries, not the viewer's timezone).
 */
export const businessSecondsBetween = (startIso: string | null | undefined, endIso: string | null | undefined): number => {
  if (!startIso || !endIso) return 0;
  const start = moment.parseZone(startIso);
  const end = moment.parseZone(endIso);
  if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return 0;

  // Walk day-by-day in the start's offset, summing weekday overlap with [start, end].
  let businessSeconds = 0;
  // Cursor at the start of the day containing `start`, in the start's fixed offset.
  const cursor = start.clone().startOf('day');
  const endMs = end.valueOf();

  while (cursor.valueOf() < endMs) {
    const nextDay = cursor.clone().add(1, 'day');
    const day = cursor.day(); // 0 = Sun, 6 = Sat (in the start's offset)
    if (day !== 0 && day !== 6) {
      // Overlap of this weekday with [start, end], in wall-clock seconds.
      const segStart = Math.max(cursor.valueOf(), start.valueOf());
      const segEnd = Math.min(nextDay.valueOf(), endMs);
      if (segEnd > segStart) {
        // Multiply before dividing so a whole-second weekday span stays exact (avoids the
        // 8/24 repeating-fraction error that Math.floor below would otherwise bite into at
        // the strict `> SLA` breach boundary). ms → business-seconds.
        businessSeconds += ((segEnd - segStart) * BUSINESS_HOURS_PER_DAY) / (24 * 1000);
      }
    }
    cursor.add(1, 'day');
  }
  // Floor (not round): never inflate elapsed business time into a false SLA breach at the
  // boundary — a span that is genuinely just under the budget must stay under it.
  return Math.floor(businessSeconds);
};

export const getRemainingTimeDetails = (futureDate: string) => {
  const currentDate = moment();
  const duration = moment.duration(moment(futureDate).diff(currentDate));
  let message = '';
  switch (true) {
    case duration.asDays() >= 1:
      message = `${Math.floor(duration.asDays())} day(s) left`;
      break;
    case duration.asHours() >= 1:
      message = `${Math.floor(duration.asHours())} hour(s) left`;
      break;
    case duration.asMinutes() >= 1:
      message = `${Math.floor(duration.asMinutes())} minute(s) left`;
      break;
    case duration.asSeconds() >= 0:
      message = `${Math.floor(duration.asSeconds())} second(s) left`;
      break;
  }
  return message;
};

export const getTimeAgoDetails = (pastDate: string) => {
  const currentDate = moment();
  const duration = moment.duration(currentDate.diff(moment(pastDate)));
  let message = '';

  if (duration.asDays() >= 1) {
    message = `${Math.floor(duration.asDays())} day(s) ago`;
  } else if (duration.asHours() >= 1) {
    message = `${Math.floor(duration.asHours())} hour(s) ago`;
  } else if (duration.asMinutes() >= 1) {
    message = `${Math.floor(duration.asMinutes())} minute(s) ago`;
  } else {
    message = `${Math.floor(duration.asSeconds())} second(s) ago`;
  }

  return message;
};

export const getMinutesAgoDetails = (minutes: number, isSeconds = false) => {
  let duration = moment.duration();
  if (isSeconds) {
    duration = moment.duration(minutes, 'seconds');
  } else {
    duration = moment.duration(minutes, 'minutes');
  }
  let message = '';

  if (duration.asDays() >= 1) {
    const days = Math.floor(duration.asDays());
    message = `${days} day${days !== 1 ? 's' : ''}`;
  } else if (duration.asHours() >= 1) {
    const hours = Math.floor(duration.asHours());
    message = `${hours} hour${hours !== 1 ? 's' : ''}`;
  } else if (duration.asMinutes() >= 1) {
    const minutes = Math.floor(duration.asMinutes());
    message = `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else {
    if (isSeconds) {
      const seconds = Math.floor(duration.asSeconds());
      message = `${seconds} second${seconds !== 1 ? 's' : ''}`;
    } else {
      message = 'Just a moment ago';
    }
  }
  return message;
};

export const getFormattedMonthYearFromUTC = (input: string, format: string) => {
  return moment.utc(input).format(format);
};

export const getDiffInTime = (time1: moment.MomentInput, time2: moment.MomentInput, unit: DurationInputArg2) => {
  return moment(time2).diff(moment(time1), unit);
};

// Check if the input string (convert it to moment) is current Month
export const isCurrentYearAndMonth = (input: string): boolean => {
  const inputDateTime = moment(input);
  return `${inputDateTime.month() - inputDateTime.year()}` === `${moment().month() - moment().year()}`;
};

export function getLastFewMonthsIncludingCurrentMonth_AllowedByFinOps(): moment.Moment {
  const currentDate = moment();
  const lastSixMonths = currentDate.clone().subtract(FINTECH_MONTH_COUNT - 1, 'month'); // .subtract(1, 'year').add(1, 'month');
  const firstDayOfMonth = lastSixMonths.startOf('month');
  return firstDayOfMonth;
}

/**
 * Returns the first day of the month N months ago (inclusive of current month).
 * Used by User Metrics to support a 12-month data window independently of other OE modules.
 */
export function getLastFewMonthsForUserMetrics(): moment.Moment {
  const currentDate = moment();
  const startMonth = currentDate.clone().subtract(USER_METRICS_MONTH_COUNT - 1, 'month');
  return startMonth.startOf('month');
}

// using any[] for metricsParsedData array since schema is diff for each metrics
export const getCurrentAndPreviousMonth = (metricsParsedData: any[], column: string) => {
  //initializing maxDate to default value to handle empty metricsParsedData
  try {
    let maxDate = moment('9999-12-31');
    if (metricsParsedData.length > 0) {
      //maxdate in the filtered data
      maxDate = moment(metricsParsedData.reduce((a, b) => (a[column] > b[column] ? a : b))[column]);
    }
    const currentMonth = maxDate.startOf('month').format('YYYY-MM-DD');
    const previousMonth = maxDate.subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
    return [currentMonth, previousMonth];
  } catch (error: any) {
    logger.error('error while getCurrentAndPreviousMonth', error);
    throw new Error('Unable to retrieve current and previous month');
  }
};

export const getCurrentUserLocalTimeFormatted = () => {
  const localTime = moment.tz(moment.tz.guess());
  return localTime.format('YYYY-MM-DD-HH-mm-ss');
};

/**
 * Creates a Date object where UTC components match local time components.
 * This is needed for JSZip which uses getUTC* methods internally.
 * Without this adjustment, file/folder timestamps appear in UTC instead of local time.
 */
export const getLocalDateForJSZip = (): Date => {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds()));
};

// ── Generic short-form date formatters ────────────────────────────────

/**
 * "2026-07-31" → "Jul 31, 2026". Returns an em-dash for blank/invalid input.
 * Use for any user-facing short date label that should match the rest of
 * the FinSuite UI (the body of `getReadableDateFromDateTime` produces the
 * same `ll` moment format).
 */
export const formatHumanDate = (iso: string | undefined | null): string => {
  if (!iso) return '—';
  if (!moment(String(iso)).isValid()) return '—';
  return getReadableDateFromDateTime(String(iso));
};

/**
 * UTC-safe date formatter. Slices the first 10 characters of the ISO string
 * and formats as locale short date (e.g. "May 1, 2026") without timezone conversion.
 * Use for Kingpin dates that arrive as "2026-05-01T00:00:00Z" from DDB.
 */
export const formatDateUtc = (iso: string | undefined | null): string => {
  if (!iso) return '—';
  const m = moment.utc(String(iso).slice(0, 10), 'YYYY-MM-DD', true);
  return m.isValid() ? m.format('ll') : '—';
};

/**
 * "2026-10" → "Oct 26". Use for compact month axis labels and dropdown
 * options where chronological-string sort matters (`YYYY-MM` sorts).
 * Returns the input string unchanged when it doesn't match `YYYY-MM`.
 */
export const formatMonthShort = (yyyyMm: string): string => {
  const parsed = moment(yyyyMm, 'YYYY-MM', true);
  if (!parsed.isValid()) return yyyyMm;
  return parsed.format('MMM YY');
};

/**
 * "Oct" — month abbreviation from a year + zero-based month-index pair.
 * Use for charts/dropdowns that build labels from numeric month/year tuples.
 */
export const formatMonthAbbrev = (year: number, monthIndex: number): string => moment({ year, month: monthIndex }).format('MMM');

/**
 * "May 20, 2026 8:27 AM PDT" — full single-line PDT timestamp. Used by the
 * Frame page header to show the data-refreshed-at time. Returns the input
 * unchanged when it can't be parsed; em-dash when blank.
 */
export const formatFullPdtTimestamp = (iso: string | undefined | null): string => {
  if (!iso) return '—';
  const m = moment(String(iso));
  if (!m.isValid()) return String(iso);
  return m.tz('America/Los_Angeles').format('MMM D, YYYY h:mm A z');
};

/**
 * "MM/DD/YYYY" — Excel-friendly numeric date. Returns the input unchanged
 * when it can't be parsed; empty string when blank.
 */
export const formatDateMDY = (iso: string | undefined | null): string => {
  if (!iso) return '';
  const m = moment(String(iso));
  if (!m.isValid()) return String(iso);
  return m.format('MM/DD/YYYY');
};

/**
 * "MM/DD/YYYY HH:MM:SS AM" PDT — long timestamp variant for log/export
 * cells. Always renders in `America/Los_Angeles`.
 */
export const formatDateTimeMDYPdt = (iso: string | undefined | null): string => {
  if (!iso) return '';
  const m = moment(String(iso));
  if (!m.isValid()) return String(iso);
  return m.tz('America/Los_Angeles').format('MM/DD/YYYY hh:mm:ss A');
};

/** "October" — long English month name from a Date. */
export const formatLongMonthName = (date: Date): string => moment(date).format('MMMM');

// ── Generic day-count helpers ─────────────────────────────────────────

/**
 * Whole-day delta from today (00:00 local) to `iso`. Negative = overdue.
 * Returns `NaN` for blank or unparseable input. Day arithmetic uses
 * `moment().diff(other, 'days')` so DST transitions don't off-by-one.
 */
export const daysUntil = (iso: string | undefined | null): number => {
  if (!iso) return Number.NaN;
  const m = moment(String(iso));
  if (!m.isValid()) return Number.NaN;
  return m.startOf('day').diff(moment().startOf('day'), 'days');
};

/** YYYY-MM-DD string for `n` days ago (today − n). */
export const isoDateNDaysAgo = (n: number): string => moment().subtract(n, 'days').format('YYYY-MM-DD');

/** Returns `iso` shifted by `deltaDays` (can be negative) as a `YYYY-MM-DD` string. */
export const shiftIsoDate = (iso: string, deltaDays: number): string => moment(iso).add(deltaDays, 'days').format('YYYY-MM-DD');

// ── Timezone Utilities for EA Huddle Scheduling ────────────────────────

/** Get the user's current IANA timezone */
export const getUserTimezone = (): string => moment.tz.guess();

/**
 * Format a time slot for display: "Mon, Jan 15 · 2:00 PM – 2:30 PM PST"
 */
export const formatTimeSlotDisplay = (date: string, startTime: string, endTime: string, timezone?: string): string => {
  const tz = timezone || moment.tz.guess();
  const start = moment.tz(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm', tz);
  const end = moment.tz(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm', tz);
  const abbr = start.format('z');
  return `${start.format('dddd, MMMM D, YYYY')} · ${start.format('h:mm A')} – ${end.format('h:mm A')} ${abbr}`;
};

export const formatTimeSlotLocalLabel = (date: string, startTime: string, endTime: string, slotTimezone?: string): string => {
  const slotTz = slotTimezone || moment.tz.guess();
  const userTz = moment.tz.guess();
  const start = moment.tz(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm', slotTz).tz(userTz);
  const end = moment.tz(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm', slotTz).tz(userTz);
  const abbr = start.format('z');
  return `${start.format('MMM D')} ${start.format('h:mm A')}–${end.format('h:mm A')} ${abbr}`;
};

/**
 * Returns the next 15-minute boundary strictly after `now` in the given timezone.
 * E.g. 12:18 → "12:30", 12:00:00 → "12:15", 23:50 → null (past end of day).
 */
export function getNextStartTime(timezone: string, now?: moment.Moment): string | null {
  const current = now ? now.clone().tz(timezone) : moment.tz(timezone);
  const totalMinutes = current.hour() * 60 + current.minute();
  const nextSlot = (Math.floor(totalMinutes / 15) + 1) * 15;
  if (nextSlot >= 1440) return null;
  return `${String(Math.floor(nextSlot / 60)).padStart(2, '0')}:${String(nextSlot % 60).padStart(2, '0')}`;
}

/**
 * Returns the minimum allowed start time (HH:mm) when the selected date is today
 * in the given timezone. Returns null for future dates (no constraint).
 */
export function getMinStartTime(date: string, timezone: string, now?: moment.Moment): string | null {
  const current = now ? now.clone().tz(timezone) : moment.tz(timezone);
  if (date !== current.format('YYYY-MM-DD')) return null;
  return getNextStartTime(timezone, current);
}

/**
 * Returns the minimum allowed end time (HH:mm). Must be after both the selected
 * start time and the current time (when the date is today).
 * Returns the start time itself as the floor when date is not today.
 */
export function getMinEndTime(date: string, timezone: string, startTime: string, now?: moment.Moment): string | null {
  const minStart = getMinStartTime(date, timezone, now);
  if (!startTime && !minStart) return null;
  if (!startTime) return minStart;
  if (!minStart) return startTime;
  return minStart > startTime ? minStart : startTime;
}
